import React, { useState, useEffect, useCallback, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import MoonLoader from "react-spinners/MoonLoader";
import "react-perfect-scrollbar/dist/css/styles.css";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  Badge,
  Spinner,
  Alert,
  Dropdown,
} from "react-bootstrap";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconDotsVertical,
  IconPhone,
  IconGripVertical,
} from "@tabler/icons-react";
import { api } from "../../../api/Middleware";
import { COLUMNS_URL, CONTACTS_URL } from "../../../api/Urls";
import "./styles/contactboard.css";
import {
  fetchData,
  handleBulkDelete,
  handleSaveContact,
  fetchContactsWithPagination,
  loadMoreContacts,
} from "./services";
import NumberFormat from "react-number-format";
import { getRandomColor } from "./label-utils";
import { ColorPicker, ContactLabel } from "./Label";
import { formatPhoneNumber } from "../../../helpers/Helpers";

// Schema de validação para o formulário de contato
const contactSchema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  phone: yup.string().required("Telefone é obrigatório"),
  conversation_history: yup.string().nullable(),
  bot_status: yup.number(),
  label: yup.string().nullable(),
});

// Schema de validação para o formulário de coluna
const columnSchema = yup.object().shape({
  name: yup.string().required("Nome da coluna é obrigatório"),
});

// Função auxiliar para formatação de data
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// Componente ContactCard - Representa um card de contato (corrigido)
const ContactCard = ({
  contact,
  index,
  onEditContact,
  onToggleSelect,
  isSelected,
}) => {
  const [showCheckbox, setShowCheckbox] = useState(false);

  return (
    <Draggable draggableId={`contact-${contact.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`contact-card ${
            snapshot.isDragging ? "is-dragging" : ""
          } ${isSelected ? "is-selected" : ""}`}
          onMouseEnter={() => setShowCheckbox(true)}
          onMouseLeave={() => !isSelected && setShowCheckbox(false)}
        >
          {/* Área de arrastar (toda a superfície do card) */}
          <div
            {...provided.dragHandleProps}
            className="contact-card-drag-handle"
          >
            {/* Checkbox para seleção */}
            <div
              className={`contact-card-checkbox ${
                showCheckbox || isSelected ? "visible" : ""
              }`}
            >
              <Form.Check
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect(contact.id);
                }}
              />
            </div>

            {/* Conteúdo do card que responde ao clique para editar */}
            <div
              className="contact-card-content"
              onClick={() => onEditContact(contact)}
            >
              <div className="contact-card-header">
                <h5>{contact.name || contact.phone}</h5>
                {contact.label && contact.label.name && (
                  <div className="ms-auto">
                    <ContactLabel label={contact.label} />
                  </div>
                )}
              </div>

              <div className="contact-card-body">
                <div className="contact-info">
                  <div className="contact-info-item">
                    <IconPhone size={14} />
                    <span>{contact.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

// Componente Column - Coluna de contatos com scroll infinito
const Column = ({
  column,
  contacts,
  onEditContact,
  onEditColumn,
  onDeleteColumn,
  selectedContacts,
  onToggleSelect,
  index,
  onLoadMore,
  isLoadingMore,
  hasMoreContacts,
}) => {
  const scrollRef = useRef(null);

  // Função para detectar scroll próximo ao final
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isLoadingMore || !hasMoreContacts) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const threshold = 100; // pixels antes do final para começar a carregar

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      onLoadMore(column.id);
    }
  }, [column.id, onLoadMore, isLoadingMore, hasMoreContacts]);

  // Adicionar event listener para scroll
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        scrollElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <Draggable draggableId={`column-${column.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`contact-column-wrapper ${
            snapshot.isDragging ? "column-is-dragging" : ""
          }`}
        >
          {/* Cabeçalho da coluna com alça de arrasto */}
          <div className="column-header bg-primary">
            <div className="column-drag-handle" {...provided.dragHandleProps}>
              <IconGripVertical size={18} color="#fff" />
            </div>

            <h4 className="text-white">{column.name}</h4>

            <Dropdown align="end">
              <Dropdown.Toggle
                variant="link"
                className="column-menu-toggle p-0"
              >
                <IconDotsVertical size={18} color="#fff" />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => onEditColumn(column)}>
                  <IconEdit size={16} className="me-2" />
                  Editar
                </Dropdown.Item>

                <Dropdown.Item
                  onClick={() => onDeleteColumn(column)}
                  className="text-danger"
                >
                  <IconTrash size={16} className="me-2" />
                  Excluir
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Área para os contatos com scroll infinito */}
          <Droppable droppableId={`column-${column.id}`} type="CONTACT">
            {(dropProvided, dropSnapshot) => (
              <div
                className={`contact-column column-content ${
                  dropSnapshot.isDraggingOver ? "is-dragging-over" : ""
                }`}
                ref={(el) => {
                  dropProvided.innerRef(el);
                  scrollRef.current = el;
                }}
                {...dropProvided.droppableProps}
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                {contacts.map((contact, contactIndex) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    index={contactIndex}
                    onEditContact={onEditContact}
                    onToggleSelect={onToggleSelect}
                    isSelected={selectedContacts.includes(contact.id)}
                  />
                ))}

                {/* Loading indicator para mais contatos */}
                {isLoadingMore && (
                  <div className="d-flex justify-content-center py-3">
                    <MoonLoader color="#007bff" size={20} />
                  </div>
                )}

                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

const ContactFormModal = ({ show, contact, onClose, onSave }) => {
  // Estado local para a label
  const [labelName, setLabelName] = useState("");
  const [labelColor, setLabelColor] = useState(getRandomColor());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      name: "",
      phone: "",
      bot_status: 1,
      conversation_history: "",
      conversation_history_ai: ""
    },
  });

  // Resetar o formulário e também os campos da label
  useEffect(() => {
    if (contact) {
      reset({
        name: contact.name || "",
        phone: formatPhoneNumber(contact.phone) || "",
        bot_status: 1,
        conversation_history: contact.conversation_history || "",
        conversation_history_ai: contact.conversation_history_ai || "",
      });

      // Se o contato já tiver uma label, carregá-la
      if (contact.label) {
        setLabelName(contact.label.name || "");
        setLabelColor(contact.label.color || getRandomColor());
      } else {
        // Caso contrário, resetar os campos de label
        setLabelName("");
        setLabelColor(getRandomColor());
      }
    }
  }, [contact, reset]);

  const formatPhoneForAPI = (phoneNumber) => {
    if (!phoneNumber) return "";
    const numbersOnly = phoneNumber.replace(/\D/g, "");
    if (numbersOnly && !numbersOnly.startsWith("55")) {
      return `+55${numbersOnly}`;
    } else if (numbersOnly.startsWith("55")) {
      return `+${numbersOnly}`;
    }
    return `+${numbersOnly}`;
  };

  // Formatação de propriedades (mantida como estava)
  const formatProperties = () => {
    if (!contact || !contact.properties || !contact.properties.length) {
      return [];
    }

    return contact.properties.map((prop) => {
      let displayValue = prop.value;
      try {
        if (prop.value.startsWith("[") || prop.value.startsWith("{")) {
          const parsed = JSON.parse(prop.value);
          displayValue = Array.isArray(parsed) ? parsed.join(", ") : prop.value;
        }
      } catch (e) {
        // Manter valor original se não for JSON válido
      }

      return {
        ...prop,
        displayValue,
        formattedDate: formatDate(prop.created_at),
      };
    });
  };

  const properties = contact ? formatProperties() : [];

  // Função para lidar com a remoção da label
  const handleRemoveLabel = () => {
    setLabelName("");
    setLabelColor(getRandomColor());
  };

  // Função para atualizar a cor da label
  const handleColorChange = (color) => {
    setLabelColor(color);
  };

  const onSubmit = (data) => {
    // Criar o objeto label apenas se houver um nome definido
    const labelData = labelName.trim()
      ? { name: labelName.trim(), color: labelColor }
      : null;

    const formattedData = {
      ...contact,
      ...data,
      phone: formatPhoneForAPI(data.phone),
      label: labelData,
    };

    onSave(formattedData);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Detalhes do Contato</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Nome</Form.Label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Form.Control {...field} isInvalid={!!errors.name} />
                )}
              />
              {errors.name && (
                <Form.Control.Feedback type="invalid">
                  {errors.name.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Telefone</Form.Label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <NumberFormat
                    format="(##) #####-####"
                    className={`form-control ${
                      errors.phone ? "is-invalid" : ""
                    }`}
                    placeholder={"Telefone"}
                    onValueChange={(values) => {
                      field.onChange(values.value);
                    }}
                    value={field.value}
                  />
                )}
              />
              {errors.phone && (
                <Form.Control.Feedback type="invalid">
                  {errors.phone.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          <Row>
            {/* Nova seção para configuração de label */}
            <Form.Group className="col-md-8 mb-4">
              <Form.Label>Etiqueta</Form.Label>
              <div className="d-flex align-items-center mb-2">
                <Form.Control
                  placeholder="Nome da etiqueta"
                  value={labelName}
                  onChange={(e) => setLabelName(e.target.value)}
                  style={{ maxWidth: "300px", marginRight: "10px" }}
                />
                {labelName && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleRemoveLabel}
                  >
                    Remover
                  </Button>
                )}
              </div>

              {/* Seletor de cores */}
              {labelName && (
                <div className="mt-2">
                  <Form.Label>Cor da etiqueta:</Form.Label>
                  <ColorPicker
                    selectedColor={labelColor}
                    onChange={handleColorChange}
                  />

                  {/* Prévia da etiqueta */}
                  <div className="mt-3">
                    <Form.Label>Prévia:</Form.Label>
                    <div>
                      <ContactLabel
                        label={{ name: labelName, color: labelColor }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </Form.Group>

            <Form.Group className="col-md-4 mt-2 mb-3 align-items-center">
              <Form.Label>Status do agente</Form.Label>
              <Controller
                name="bot_status"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Form.Check
                    type="checkbox"
                    label="Agente Ativo"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            </Form.Group>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Follow-Up Humano</Form.Label>
            <Controller
              name="conversation_history"
              control={control}
              render={({ field }) => (
                <Form.Control as="textarea" rows={8} {...field} />
              )}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Follow-Up IA</Form.Label>
            <Controller
              name="conversation_history_ai"
              control={control}
              render={({ field }) => (
                <Form.Control as="textarea" rows={8} {...field} />
              )}
              disabled
            />
          </Form.Group>

          {contact && (
            <div className="text-muted small mb-3">
              Última atualização: {formatDate(contact.last_update)}
            </div>
          )}

          {properties.length > 0 && (
            <div className="mt-4">
              <h5 className="mb-3">Propriedades do Contato</h5>

              <Row>
                {properties.map((prop, idx) => (
                  <Col md={6} key={idx} className="mb-3">
                    <Card className="property-card mb-1">
                      <Card.Body className="property-details">
                        <Card.Title className="property-name fs-16 mb-1 text-capitalize">
                          {prop.property.replace(/_/g, " ")}
                        </Card.Title>
                        <Card.Text className="text-capitalize">
                          {prop.displayValue}
                        </Card.Text>
                        <small className="text-muted">
                          Registrado em: {prop.formattedDate}
                        </small>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSubmit(onSubmit)}>
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Componente ColumnFormModal - Modal para adicionar/editar coluna
const ColumnFormModal = ({ show, column, onClose, onSave }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(columnSchema),
    defaultValues: {
      name: "",
    },
  });

  // Resetar formulário quando coluna muda
  useEffect(() => {
    reset({
      name: column?.name || "",
    });
  }, [column, reset]);

  const onSubmit = (data) => {
    onSave({ ...(column || {}), ...data });
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{column ? "Editar Coluna" : "Nova Coluna"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-3">
            <Form.Label>Nome da Coluna</Form.Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Form.Control {...field} autoFocus isInvalid={!!errors.name} />
              )}
            />
            {errors.name && (
              <Form.Control.Feedback type="invalid">
                {errors.name.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSubmit(onSubmit)}>
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const DragDropBoard = ({
  columns,
  contactsByColumn,
  handleDragEnd,
  onLoadMore,
  loadingStates,
  hasMoreByColumn,
  ...props
}) => {
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="board" type="COLUMN" direction="horizontal">
        {(provided) => (
          <div
            className="contact-board"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {columns.map((column, index) => (
              <Column
                key={`column-${column.id}`}
                column={column}
                index={index}
                contacts={contactsByColumn[column.id] || []}
                onEditContact={props.onEditContact}
                onEditColumn={props.onEditColumn}
                onDeleteColumn={props.onDeleteColumn}
                selectedContacts={props.selectedContacts}
                onToggleSelect={props.onToggleSelect}
                onLoadMore={onLoadMore}
                isLoadingMore={loadingStates[column.id] || false}
                hasMoreContacts={hasMoreByColumn[column.id] || false}
              />
            ))}
            {provided.placeholder}

            {/* Botão para adicionar coluna */}
            <div className="add-column-button" onClick={props.onAddColumn}>
              <div className="add-column-content">
                <IconPlus size={24} />
                <span>Adicionar Coluna</span>
              </div>
            </div>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const ContactBoard = () => {
  // Estados para dados
  const [columns, setColumns] = useState([]);
  const [contactsByColumn, setContactsByColumn] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para paginação e scroll infinito
  const [offsetByColumn, setOffsetByColumn] = useState({});
  const [hasMoreByColumn, setHasMoreByColumn] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [totalContactsByColumn, setTotalContactsByColumn] = useState({});

  // Estados para modais e seleção
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [currentColumn, setCurrentColumn] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Carregar dados iniciais
  useEffect(() => {
    fetchData(
      setColumns,
      setContactsByColumn,
      setLoading,
      setError,
      setOffsetByColumn,
      setHasMoreByColumn,
      setTotalContactsByColumn
    );
  }, []);

  // Função para carregar mais contatos de uma coluna específica
  const handleLoadMore = useCallback(
    async (columnId) => {
      if (loadingStates[columnId] || !hasMoreByColumn[columnId]) {
        return;
      }

      setLoadingStates((prev) => ({ ...prev, [columnId]: true }));

      try {
        const currentOffset = offsetByColumn[columnId] || 0;
        const newOffset = currentOffset + 10;

        const response = await loadMoreContacts(columnId, newOffset, 10);

        if (response.results && response.results.length > 0) {
          // Ordenar os novos contatos
          const sortedContacts = response.results.sort(
            (a, b) => a.order - b.order
          );

          // Adicionar novos contatos aos existentes
          setContactsByColumn((prev) => ({
            ...prev,
            [columnId]: [...(prev[columnId] || []), ...sortedContacts],
          }));

          // Atualizar offset
          setOffsetByColumn((prev) => ({
            ...prev,
            [columnId]: newOffset,
          }));

          // Verificar se há mais contatos
          setHasMoreByColumn((prev) => ({
            ...prev,
            [columnId]: response.has_next,
          }));
        } else {
          // Não há mais contatos
          setHasMoreByColumn((prev) => ({
            ...prev,
            [columnId]: false,
          }));
        }
      } catch (err) {
        console.error(
          `Erro ao carregar mais contatos da coluna ${columnId}:`,
          err
        );
        setHasMoreByColumn((prev) => ({
          ...prev,
          [columnId]: false,
        }));
      } finally {
        setLoadingStates((prev) => ({ ...prev, [columnId]: false }));
      }
    },
    [offsetByColumn, hasMoreByColumn, loadingStates]
  );

  // Manipular drag and drop
  const handleDragEnd = async (result) => {
    const { source, destination, type } = result;

    // Se não há destino ou o item voltou para o mesmo lugar
    if (!destination) {
      return;
    }

    // Verificar se o item realmente mudou de posição
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // Se estamos movendo uma coluna
    if (type === "COLUMN") {
      const newColumns = Array.from(columns);
      const [movedColumn] = newColumns.splice(source.index, 1);
      newColumns.splice(destination.index, 0, movedColumn);

      setColumns(newColumns);

      try {
        const reorderData = newColumns.map((column, index) => ({
          id: column.id,
          order: index,
        }));

        await api.post(`${COLUMNS_URL}reorder/`, reorderData);

        Swal.fire({
          title: "Sucesso!",
          text: "Colunas reordenadas com sucesso!",
          icon: "success",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } catch (err) {
        console.error("Erro ao reordenar colunas:", err);
        fetchData(
          setColumns,
          setContactsByColumn,
          setLoading,
          setError,
          setOffsetByColumn,
          setHasMoreByColumn,
          setTotalContactsByColumn
        );

        Swal.fire({
          title: "Erro!",
          text: "Não foi possível reordenar as colunas.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
      return;
    }

    // Se estamos movendo um contato
    if (type === "CONTACT") {
      const sourceColumnId = parseInt(source.droppableId.split("-")[1]);
      const destColumnId = parseInt(destination.droppableId.split("-")[1]);

      // Validações básicas
      if (
        !contactsByColumn[sourceColumnId] ||
        !contactsByColumn[destColumnId] ||
        source.index >= contactsByColumn[sourceColumnId].length
      ) {
        console.error("Erro na validação dos dados de drag and drop");
        return;
      }

      // Obter o contato que foi movido
      const movedContact = contactsByColumn[sourceColumnId][source.index];

      // Atualizar estado local otimisticamente
      const newContactsByColumn = { ...contactsByColumn };
      const sourceContacts = [...newContactsByColumn[sourceColumnId]];
      const [contactToMove] = sourceContacts.splice(source.index, 1);

      if (sourceColumnId === destColumnId) {
        // Reordenação na mesma coluna
        sourceContacts.splice(destination.index, 0, contactToMove);
        newContactsByColumn[sourceColumnId] = sourceContacts;
      } else {
        // Mudança de coluna
        const destContacts = [...newContactsByColumn[destColumnId]];
        destContacts.splice(destination.index, 0, contactToMove);

        newContactsByColumn[sourceColumnId] = sourceContacts;
        newContactsByColumn[destColumnId] = destContacts;

        contactToMove.column = destColumnId;
      }

      setContactsByColumn(newContactsByColumn);

      try {
        // CORREÇÃO PRINCIPAL: Enviar apenas o contato que foi movido
        const reorderData = [
          {
            id: movedContact.id,
            columnId: destColumnId,
            order: destination.index,
          },
        ];

        await api.post(`${CONTACTS_URL}reorder/`, reorderData);
      } catch (err) {
        console.error("Erro ao mover contato:", err);

        // Rollback em caso de erro
        fetchData(
          setColumns,
          setContactsByColumn,
          setLoading,
          setError,
          setOffsetByColumn,
          setHasMoreByColumn,
          setTotalContactsByColumn
        );

        Swal.fire({
          title: "Erro!",
          text: "Não foi possível mover o contato.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  // Funções para gerenciar contatos
  const handleEditContact = (contact) => {
    setCurrentContact(contact);
    setContactModalOpen(true);
  };

  // Funções para gerenciar colunas
  const handleAddColumn = () => {
    setCurrentColumn(null);
    setColumnModalOpen(true);
  };

  const handleEditColumn = (column) => {
    setCurrentColumn(column);
    setColumnModalOpen(true);
  };

  const handleSaveColumn = async (columnData) => {
    try {
      let response;

      if (columnData.id) {
        // Atualizar coluna existente
        response = await api.patch(`${COLUMNS_URL}${columnData.id}/`, {
          name: columnData.name,
        });

        setColumns(
          columns.map((col) =>
            col.id === columnData.id ? { ...col, name: columnData.name } : col
          )
        );

        Swal.fire({
          title: "Sucesso!",
          text: "Coluna atualizada com sucesso!",
          icon: "success",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        // Criar nova coluna
        const maxOrder =
          columns.length > 0
            ? Math.max(...columns.map((col) => col.order))
            : -1;

        response = await api.post(`${COLUMNS_URL}`, {
          name: columnData.name,
          order: maxOrder + 1,
        });

        const newColumn = response.data;

        setColumns([...columns, newColumn]);
        setContactsByColumn({
          ...contactsByColumn,
          [newColumn.id]: [],
        });

        // Inicializar estados de paginação para a nova coluna
        setOffsetByColumn((prev) => ({ ...prev, [newColumn.id]: 0 }));
        setHasMoreByColumn((prev) => ({ ...prev, [newColumn.id]: false }));
        setTotalContactsByColumn((prev) => ({ ...prev, [newColumn.id]: 0 }));

        Swal.fire({
          title: "Sucesso!",
          text: "Nova coluna criada com sucesso!",
          icon: "success",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }

      setColumnModalOpen(false);
    } catch (err) {
      console.error("Erro ao salvar coluna:", err);

      Swal.fire({
        title: "Erro!",
        text: "Não foi possível salvar a coluna.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDeleteColumn = (column) => {
    // Verificar se a coluna tem contatos
    if (contactsByColumn[column.id]?.length > 0) {
      Swal.fire({
        title: "Não é possível excluir",
        text: "Não é possível excluir uma coluna que contém contatos.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    // Verificar se é a única coluna - não permitir excluir
    if (columns.length === 1) {
      Swal.fire({
        title: "Não é possível excluir",
        text: "É necessário ter pelo menos uma coluna. Crie outra antes de excluir esta.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    Swal.fire({
      title: "Confirmar exclusão",
      text: "Esta ação não poderá ser revertida! Deseja excluir esta coluna?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`${COLUMNS_URL}${column.id}/`);

          // Atualizar estado local
          setColumns(columns.filter((col) => col.id !== column.id));

          const newContactsByColumn = { ...contactsByColumn };
          delete newContactsByColumn[column.id];
          setContactsByColumn(newContactsByColumn);

          // Limpar estados de paginação
          setOffsetByColumn((prev) => {
            const newOffsets = { ...prev };
            delete newOffsets[column.id];
            return newOffsets;
          });
          setHasMoreByColumn((prev) => {
            const newHasMore = { ...prev };
            delete newHasMore[column.id];
            return newHasMore;
          });
          setTotalContactsByColumn((prev) => {
            const newTotals = { ...prev };
            delete newTotals[column.id];
            return newTotals;
          });

          Swal.fire({
            title: "Excluída!",
            text: "A coluna foi excluída com sucesso.",
            icon: "success",
            confirmButtonText: "OK",
          });
        } catch (err) {
          console.error("Erro ao excluir coluna:", err);

          Swal.fire({
            title: "Erro!",
            text: "Não foi possível excluir a coluna.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleToggleSelect = (contactId) => {
    setSelectedContacts((prev) => {
      const isSelected = prev.includes(contactId);
      const newSelection = isSelected
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId];

      return newSelection;
    });
  };

  return (
    <div className="contact-board-container main-container">
      {/* Cabeçalho e ações */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="ml-2 mb-0">CRM </h2>

            <div>
              {selectedContacts.length > 0 && (
                <Button
                  variant="danger"
                  className="me-2"
                  onClick={() =>
                    handleBulkDelete(
                      selectedContacts,
                      contactsByColumn,
                      setContactsByColumn,
                      setSelectedContacts
                    )
                  }
                >
                  <IconTrash size={16} className="me-1" />
                  Excluir Selecionados ({selectedContacts.length})
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Indicador de total de contatos */}

      {/* Mensagem de erro */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {/* Loading spinner */}
      {loading ? (
        <div className="d-flex justify-content-center mt-5">
          <MoonLoader color="#007bff" size={50} />
        </div>
      ) : columns.length === 0 ? (
        // Caso ainda não haja colunas
        <div className="d-flex flex-column align-items-center mt-5">
          <Alert variant="info">
            Nenhuma coluna encontrada. Clique no botão abaixo para criar sua
            primeira coluna.
          </Alert>
          <Button variant="primary" className="mt-3" onClick={handleAddColumn}>
            <IconPlus size={16} className="me-1" />
            Criar Coluna
          </Button>
        </div>
      ) : (
        // Board com drag and drop - Agora com scroll infinito
        <DragDropBoard
          columns={columns}
          contactsByColumn={contactsByColumn}
          handleDragEnd={handleDragEnd}
          onEditContact={handleEditContact}
          onEditColumn={handleEditColumn}
          onDeleteColumn={handleDeleteColumn}
          selectedContacts={selectedContacts}
          onToggleSelect={handleToggleSelect}
          onAddColumn={handleAddColumn}
          onLoadMore={handleLoadMore}
          loadingStates={loadingStates}
          hasMoreByColumn={hasMoreByColumn}
        />
      )}

      {/* Modal para edição de contato */}
      <ContactFormModal
        show={contactModalOpen}
        contact={currentContact}
        onClose={() => {
          setContactModalOpen(false);
          setCurrentContact(null);
        }}
        onSave={(updatedContactData) =>
          handleSaveContact(
            updatedContactData,
            contactsByColumn,
            setContactsByColumn,
            setContactModalOpen,
            setCurrentContact
          )
        }
      />

      {/* Modal para adicionar/editar coluna */}
      <ColumnFormModal
        show={columnModalOpen}
        column={currentColumn}
        onClose={() => {
          setColumnModalOpen(false);
          setCurrentColumn(null);
        }}
        onSave={handleSaveColumn}
      />
    </div>
  );
};

export default ContactBoard;
